// The whole deal, derived from one seed.
//
// Cartographers has a property none of its siblings do: the entire card
// sequence of a game - all four seasons of it - is a pure function of the
// shuffle seed. Season length depends only on the time values of revealed
// cards (rules p8), reshuffles depend only on what was revealed (p9), and
// nothing a player draws on a map feeds back into the deck. So instead of
// syncing deck state, every client derives this script from the seed in the
// room and they can never disagree.
//
// The script is a flat list of TURNS - the moments players draw something:
//   { type: 'explore', cardId, season, ruins, ruinsIds, timeAfter }
//   { type: 'ambush',  cardId, season, direction, timeAfter }
// Ruins cards are not turns (revealing one "immediately reveal[s] another
// card", p5); they ride along as `ruinsIds` + the `ruins` flag on the explore
// turn they constrain.

import { createRngState, shuffle } from './rng/rng';
import {
  EXPLORE_CARDS, AMBUSH_CARDS, SEASONS, SCORING_FAMILIES,
} from './cards';

export const buildScript = (seed) => {
  let rng = createRngState(seed);

  // Setup, rules p4: one scoring card drawn per family, then the four are
  // dealt below edicts A-D - both draws random.
  const drawn = [];
  SCORING_FAMILIES.forEach((fam) => {
    let pick;
    [pick, rng] = shuffle(fam.cards, rng);
    drawn.push(pick[0].key);
  });
  let dealt;
  [dealt, rng] = shuffle(drawn, rng);
  const edicts = {
    A: dealt[0], B: dealt[1], C: dealt[2], D: dealt[3],
  };

  let ambushOrder;
  [ambushOrder, rng] = shuffle(AMBUSH_CARDS.map((c) => c.id), rng);

  const exploreIds = EXPLORE_CARDS.map((c) => c.id);
  const ambushById = (id) => AMBUSH_CARDS.find((c) => c.id === id);

  const turns = [];
  const seasons = [];
  // Ambush cards not yet revealed stay in the deck across seasons and stack
  // up (rules p9's note); revealed ones leave the game.
  let carriedAmbushes = [];

  SEASONS.forEach((season, seasonIndex) => {
    const pool = [...exploreIds, ...carriedAmbushes, ambushOrder[seasonIndex]];
    let deck;
    [deck, rng] = shuffle(pool, rng);

    const firstTurn = turns.length;
    let time = 0;
    let ruinsIds = [];
    carriedAmbushes = [];
    let i = 0;
    while (time < season.threshold) {
      const cardId = deck[i];
      i += 1;
      const explore = EXPLORE_CARDS.find((c) => c.id === cardId);
      if (explore?.ruins) {
        ruinsIds.push(cardId);
      } else if (explore) {
        time += explore.time;
        turns.push({
          type: 'explore',
          cardId,
          season: seasonIndex,
          ruins: ruinsIds.length > 0,
          ruinsIds,
          timeAfter: time,
        });
        ruinsIds = [];
      } else {
        // An ambush resolves as its own drawing moment; a pending ruins card
        // waits for the next explore card (rules p7's note).
        const ambush = ambushById(cardId);
        turns.push({
          type: 'ambush',
          cardId,
          season: seasonIndex,
          direction: ambush.direction,
          timeAfter: time,
        });
      }
    }
    // Ambushes still buried when the season ends carry into the next deck.
    deck.slice(i).forEach((cardId) => {
      if (ambushById(cardId)) carriedAmbushes.push(cardId);
    });

    seasons.push({
      ...season,
      seasonIndex,
      firstTurn,
      turnCount: turns.length - firstTurn,
    });
  });

  return { edicts, turns, seasons };
};
